import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ExternalLink, ShieldCheck, Globe } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import NextBestAction from "@/components/NextBestAction";
import usePageSEO from "@/hooks/usePageSEO";
import { CARRIERS_BY_SLUG, CARRIERS } from "@/data/carriers";
import { CARRIER_LOGOS } from "@/data/carrierLogos";

/**
 * Phase 14.1 — logo header. Real logo when available, typographic fallback
 * on image error or missing registry entry. ~240px max-width, centered.
 */
function CarrierLogoHeader({ carrier }) {
  const logo = CARRIER_LOGOS[carrier.slug];
  const [errored, setErrored] = useState(false);
  const showImg = logo && !errored;
  if (showImg) {
    return (
      <div className="mx-auto flex items-center justify-center" style={{ minHeight: 96 }}>
        <img
          src={logo.src}
          alt={`${carrier.name} logo`}
          data-testid={`carrier-detail-logo-${carrier.slug}`}
          onError={() => setErrored(true)}
          className="w-auto h-auto object-contain"
          style={{ maxWidth: 240, maxHeight: 96 }}
        />
      </div>
    );
  }
  return (
    <div
      className="mx-auto w-[240px] h-[96px] flex items-center justify-center"
      data-testid={`carrier-detail-logo-fallback-${carrier.slug}`}
    >
      <div className="text-center">
        <div className="font-serif-display text-3xl text-lw-navy leading-tight">
          {carrier.name.split(" ")[0]}
        </div>
        {carrier.name.split(" ").slice(1).join(" ") && (
          <div className="font-sans-ui text-[0.68rem] tracking-[0.22em] uppercase text-lw-gold mt-1">
            {carrier.name.split(" ").slice(1).join(" ")}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Phase 13.1 — dynamic carrier detail route `/carriers/[slug]`.
 *
 * Shows:
 *  - Carrier logo-tile (typographic; brand-safe)
 *  - Factual overview (no marketing claims)
 *  - Product lines offered through Lifespan Wealth Partners
 *  - NAIC-compliant disclaimer: "Products issued by [LegalName]. Not an
 *    endorsement. Availability varies by state and underwriting."
 *  - CTA that opens /apply with the carrier pre-selected
 */
export default function CarrierDetail() {
  const { slug } = useParams();
  const carrier = CARRIERS_BY_SLUG[slug];

  usePageSEO({
    title: carrier ? `${carrier.name} — Products through Lifespan Wealth Partners` : "Carrier",
    description: carrier
      ? `${carrier.name} product lines offered through Lifespan Wealth Partners — ${carrier.productLines.length} product lines. Availability varies by state.`
      : "Carrier not found.",
    jsonLd: carrier
      ? {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: carrier.legalName,
          alternateName: carrier.name,
          url: carrier.website,
          description: carrier.overview,
        }
      : null,
  });

  if (!carrier) {
    return (
      <div className="min-h-screen bg-lw-ivory">
        <Nav />
        <main className="pt-32 pb-24">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <h1 className="font-serif-display text-3xl text-lw-navy">Carrier not found</h1>
            <p className="mt-4 text-lw-muted">We don't have a carrier matching that URL.</p>
            <Link to="/carriers" className="btn-ghost-navy mt-6 text-sm">
              Back to all carriers
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid={`carrier-detail-${slug}`} className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 pb-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <nav className="text-xs text-lw-muted mb-6">
            <Link to="/" className="hover:text-lw-navy">Home</Link>
            <span className="mx-1.5">/</span>
            <Link to="/carriers" className="hover:text-lw-navy">Carriers</Link>
            <span className="mx-1.5">/</span>
            <span className="text-lw-navy">{carrier.name}</span>
          </nav>

          <div className="rounded-2xl border border-lw-navy/10 bg-white p-8 sm:p-12 text-center shadow-[0_18px_40px_-28px_rgba(10,37,64,0.25)]">
            <CarrierLogoHeader carrier={carrier} />
            <h1 className="mt-6 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
              Products available through{" "}
              <span className="italic text-lw-gold">Lifespan Wealth Partners</span>
            </h1>
            <p className="mt-5 max-w-2xl mx-auto text-base leading-relaxed text-lw-muted">
              {carrier.overview}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 lw-pill">
              <ShieldCheck size={12} /> {carrier.rating}
            </div>
          </div>

          <section className="mt-12" id="product-lines">
            <span className="lw-eyebrow">Product Lines</span>
            <h2 className="mt-3 font-serif-display text-2xl sm:text-3xl text-lw-navy">
              What we offer from {carrier.name}
            </h2>
            <ul className="mt-6 grid sm:grid-cols-2 gap-4">
              {carrier.productLines.map((p) => (
                <li
                  key={p}
                  data-testid={`carrier-product-${p}`}
                  className="lw-card group p-5"
                >
                  <Link to={`/products/${p}`} className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-serif-display text-lg text-lw-navy leading-tight">
                        {p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                      </div>
                      <div className="mt-1 text-xs text-lw-muted">
                        Learn more about this product line
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-lw-gold group-hover:translate-x-1 transition-transform" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12 rounded-2xl gradient-navy-indigo p-8 sm:p-10 text-white">
            <h3 className="font-serif-display text-2xl leading-tight">
              Ready to see if a {carrier.name} product fits your plan?
            </h3>
            <p className="mt-3 text-sm text-[#FAF7F2]/80 leading-relaxed">
              Your advisor will walk you through the carrier's underwriting, confirm product
              availability in your state, and compare it against other carriers we work with.
              No pressure, no sign-up required.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/apply?carrier=${carrier.slug}`}
                data-testid="carrier-cta-apply"
                className="btn-gold text-sm"
              >
                Schedule a conversation <ArrowRight size={14} />
              </Link>
              <a
                href={carrier.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="carrier-external-link"
                className="btn-ghost-light text-sm"
              >
                Visit carrier site <ExternalLink size={14} />
              </a>
            </div>
          </section>

          <div
            data-testid="carrier-detail-disclaimer"
            className="mt-10 rounded-xl border border-lw-navy/10 bg-lw-ivory-warm p-5 text-xs text-lw-muted leading-relaxed"
          >
            Products issued by {carrier.legalName}. Not an endorsement. Lifespan Wealth
            Partners is an independent licensed insurance agency and is not owned by,
            affiliated with, or endorsed by the carrier. Product features, availability,
            and ratings subject to change. Guarantees backed by the financial strength
            and claims-paying ability of the issuing carrier. See each carrier's
            prospectus or policy documents for full terms.
          </div>

          <NextBestAction
            pageId={`carrier-${carrier.slug}`}
            step={3}
            eyebrow="Your next best step"
            heading={`Verify ${carrier.name} fits your plan`}
            rationale={`Because you're evaluating ${carrier.name}, a quick 15-minute call with an advisor confirms state availability and compares them against 2–3 peer carriers — no application required.`}
            primary={{
              label: `Talk to an advisor about ${carrier.name}`,
              to: `/apply?carrier=${carrier.slug}`,
              testid: "nba-carrier-primary",
            }}
            secondary={{
              label: `See all products we offer from ${carrier.name}`,
              to: "#product-lines",
              testid: "nba-carrier-secondary",
            }}
          />

          <section className="mt-16">
            <span className="lw-eyebrow">Other carriers we work with</span>
            <ul className="mt-5 flex flex-wrap gap-2">
              {CARRIERS.filter((c) => c.slug !== carrier.slug)
                .slice(0, 8)
                .map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/carriers/${c.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-lw-navy border border-lw-navy/15 rounded-full px-3 py-1.5 hover:border-lw-gold hover:text-[#7f6218] transition-colors"
                    >
                      {c.name} <Globe size={12} />
                    </Link>
                  </li>
                ))}
            </ul>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
